import { useCallback, useEffect, useState } from 'react'
import { appCoreSupabase } from '../lib/supabase'
import { CheckCircle2, Clock3, FileText, RefreshCw, Wrench } from 'lucide-react'
import './JoinValidationProgressWidget.css'

function formatTime(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function normalizeDocs(context) {
  const docs = context?.docs
  return Array.isArray(docs) ? docs : []
}

export default function JoinValidationProgressWidget() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRows = useCallback(async () => {
    if (!appCoreSupabase) {
      setError('app-core Supabase unavailable')
      setLoading(false)
      return
    }

    setLoading(true)
    const { data, error: fetchError } = await appCoreSupabase
      .from('scraper_logs')
      .select('*')
      .eq('bookmaker', 'join-validation')
      .order('created_at', { ascending: false })
      .limit(12)

    if (fetchError) {
      setError(fetchError.message)
      setRows([])
    } else {
      setError(null)
      setRows(data || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchRows()
    const id = setInterval(fetchRows, 30000)
    return () => clearInterval(id)
  }, [fetchRows])

  const latest = rows[0]

  return (
    <div className="widget jvp-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Wrench size={16} />
          Join Validation Campaign
        </h3>
        <button className="widget-refresh-btn" onClick={fetchRows} title="Refresh">
          <RefreshCw size={14} className={loading ? 'jvp-spinning' : ''} />
        </button>
      </div>

      {latest && (
        <div className="jvp-summary">
          <div className="jvp-summary-row">
            <CheckCircle2 size={14} />
            <span>{latest.context?.summary || latest.message}</span>
          </div>
          <div className="jvp-summary-row muted">
            <Clock3 size={14} />
            <span>{formatTime(latest.created_at)}</span>
          </div>
        </div>
      )}

      {error && <div className="jvp-error">{error}</div>}
      {loading && rows.length === 0 && <div className="jvp-empty">Loading progress updates...</div>}
      {!loading && rows.length === 0 && !error && <div className="jvp-empty">No join-validation updates recorded yet.</div>}

      {rows.length > 0 && (
        <div className="jvp-list">
          {rows.map((row) => {
            const docs = normalizeDocs(row.context)
            return (
              <div key={row.id} className="jvp-card">
                <div className="jvp-card-header">
                  <span className={`jvp-level ${row.level || 'info'}`}>{(row.level || 'info').toUpperCase()}</span>
                  <span className="jvp-time">{formatTime(row.created_at)}</span>
                </div>
                <div className="jvp-message">{row.message}</div>
                {row.context?.summary && <div className="jvp-summary-text">{row.context.summary}</div>}
                {docs.length > 0 && (
                  <div className="jvp-docs">
                    <div className="jvp-docs-title">
                      <FileText size={12} />
                      Proof Docs
                    </div>
                    {docs.map((doc) => (
                      <div key={doc} className="jvp-doc-item">{doc}</div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
